import { Deferred, Link, usePage } from "@inertiajs/react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { useIsFirstRender, useSessionStorage } from "@uidotdev/usehooks";
import { format } from "date-fns";
import {
	AlertCircle,
	Check,
	ChevronDownIcon,
	ChevronsUpDown,
	CircleCheckBig,
	Hospital,
	LoaderIcon,
	Pencil,
	X,
} from "lucide-react";
import {
	type ComponentProps,
	createContext,
	type Dispatch,
	Fragment,
	memo,
	type SetStateAction,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { get, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BypassSlotConflictField } from "@/components/admin-portal/appointment/bypass-slot-conflict-field";
import { MitraAssignmentReasonBox } from "@/components/admin-portal/appointment/mitra-assignment-reason";
import {
	type ScheduleMode,
	ScheduleModeToggle,
} from "@/components/admin-portal/scheduling/schedule-mode-toggle";
import { SchedulePicker } from "@/components/admin-portal/scheduling/schedule-picker";
import { useDateContext } from "@/components/providers/date-provider";
import { RetroGridPattern } from "@/components/shared/retro-grid-pattern";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
	SESSION_STORAGE_FORM_KEY,
	SESSION_STORAGE_FORM_SELECTIONS_KEY,
	useAdditionalSettingsForm,
	useAppointmentSchedulingForm,
	useBookingPrice,
	useFinalStep,
	usePatientDetailsForm,
	useReviewForm,
	useStepButtons,
} from "@/hooks/admin-portal/appointment/use-appointment-form";
import {
	type TherapistOption,
	usePartnerBookingSelection,
	usePartnerNameSelection,
	usePatientReferralSource,
} from "@/hooks/admin-portal/appointment/use-appointment-utils";
import { useDraftAutosave } from "@/hooks/admin-portal/appointment/use-draft-autosave";
import { getGenderIcon } from "@/hooks/use-gender";
import {
	type AppointmentBookingSchema,
	clearPaymentExpiry,
	DEFAULT_VALUES_PACKAGE,
	DEFAULT_VALUES_SERVICE,
	type DraftBaseline,
	defineAppointmentFormDefaultValues,
	hasCompleteVisitLocation,
	hasVisitTime,
	mitraReasonFieldName,
	mitraReasonUpdates,
	mitraReasonVisits,
	toValidDate,
} from "@/lib/appointments/form";
import { preferredSchedules } from "@/lib/appointments/preferred-schedules";
import { getBadgeVariantStatus } from "@/lib/appointments/utils";
import {
	getFisiohomePartnerLabel,
	getPatientReferralLabel,
} from "@/lib/constants";
import { toIsoDate } from "@/lib/therapist-scheduling/date";
import { employmentTypeToTherapistType } from "@/lib/therapist-scheduling/employment-type";
import {
	slotSelectionToDateTime,
	slotSelectionToTherapist,
} from "@/lib/therapist-scheduling/form-adapter";
import { shouldConfirmLaterReset } from "@/lib/therapist-scheduling/therapist-change";
import type { SlotSelection } from "@/lib/therapist-scheduling/types";
import { cn, formatCurrency, populateQueryParams } from "@/lib/utils";
import type { AppointmentNewGlobalPageProps } from "@/pages/AdminPortal/Appointment/New";
import DateTimePicker from "./form/date-time";
import ExistingPatientSelection from "./form/existing-patient-selection";
import PatientBasicInfoForm from "./form/patient-basic-info";
import PatientContactForm from "./form/patient-contact";
import PatientMedicalForm from "./form/patient-medical";
import PatientRegionForm from "./form/patient-region";
import { SelectedTherapistCard } from "./form/selected-therapist-card";
import { SeriesScheduler } from "./form/series-scheduler";
import { TherapistSearchList } from "./form/therapist-search-list";
import { PreferredSchedules } from "./preferred-schedules";

export type FormMode = "new" | "series";
type FormStorage = AppointmentBookingSchema | null;
type FormStorageSelection = {
	therapist: null | TherapistOption;
	seriesTherapists: null | Record<number, TherapistOption | null>;
};

// * form provider props
interface FormProviderProps {
	children: React.ReactNode;
}

interface FormProviderState {
	mode: FormMode;
	isSuccessBooked: boolean;
	formStorage: FormStorage;
	setFormStorage: Dispatch<SetStateAction<FormStorage>>;
	formSelections: FormStorageSelection;
	setFormSelections: Dispatch<SetStateAction<FormStorageSelection>>;
	// Bumped when all visits are reset — used to remount the schedule pickers
	// (their chosen date lives in internal state, not the form).
	visitResetNonce: number;
	bumpVisitReset: () => void;
	// Sticky once true: an existing patient turned up with an incomplete stored
	// address, so the scheduling step keeps offering the location form even after
	// the admin has completed it. Lives here because the stepper unmounts the step
	// it is read on — state inside that step would forget between visits.
	isVisitLocationRevealed: boolean;
	revealVisitLocation: () => void;
	// Opens a shared confirm dialog before a reset-causing change. `onConfirm`
	// runs only on confirm. Cancelling reverts, because the caller must not have
	// applied the change yet.
	confirmReset: (params: { scope: ResetScope; onConfirm: () => void }) => void;
	/** Set when the error panel asks to focus the Mitra reason field. */
	mitraReasonTarget: number | null;
	setMitraReasonTarget: (index: number | null) => void;
}

// What a reset confirmation clears, each a full sentence under reset_dialog in the
// locale files so it translates whole instead of being stitched from fragments.
export type ResetScope =
	| "service_package_visits"
	| "patient_service_package_visits"
	| "scheduled_visits";
type VisitResetField = "service" | "package" | "gender";

// Feature flag: render the new SchedulePicker (filter -> date -> slot) instead of
// the legacy DateTimePicker + therapist selection. Absent flag -> false -> legacy.
const getUseNewSchedulePicker = (
	pageProps?: AppointmentNewGlobalPageProps["props"],
) => {
	try {
		return (
			pageProps?.adminPortal?.featureFlags?.useNewSchedulePickerEnabled ?? false
		);
	} catch {
		return false;
	}
};

const FormProviderContext = createContext<FormProviderState>({
	mode: "new",
	isSuccessBooked: false,
	formStorage: null,
	formSelections: {
		therapist: null,
		seriesTherapists: null,
	},
	setFormStorage: () => {},
	setFormSelections: () => {},
	isVisitLocationRevealed: false,
	revealVisitLocation: () => {},
	visitResetNonce: 0,
	bumpVisitReset: () => {},
	confirmReset: () => {},
	mitraReasonTarget: null,
	setMitraReasonTarget: () => {},
});

export const DraftBaselineContext = createContext<DraftBaseline>({});

export function FormProvider({ children }: FormProviderProps) {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentNewGlobalPageProps>();
	const formMode = useMemo(() => {
		const reference = globalProps.adminPortal.currentQuery?.reference;

		return reference ? ("series" as const) : ("new" as const);
	}, [globalProps.adminPortal.currentQuery?.reference]);
	const isSuccessBooked = useMemo(() => {
		const appointmentId = globalProps?.appointment?.id;
		const { queryParams } = populateQueryParams(pageURL);

		return !!queryParams?.created && !!appointmentId;
	}, [globalProps?.appointment?.id, pageURL]);

	// define the form default values and the selections storage
	const formDefaultvalues = useMemo(
		() =>
			defineAppointmentFormDefaultValues({
				mode: formMode,
				admin: globalProps.auth.currentUser,
				apptRef: globalProps.appointmentReference,
			}),
		[globalProps.auth.currentUser, globalProps.appointmentReference, formMode],
	);
	// Store form data in session storage - therapist data is now included
	// since we use database drafts for persistence
	const [formStorage, setFormStorage] = useSessionStorage<
		FormProviderState["formStorage"]
	>(SESSION_STORAGE_FORM_KEY, {
		...formDefaultvalues,
	});

	const [formSelections, setFormSelections] = useSessionStorage<
		FormProviderState["formSelections"]
	>(SESSION_STORAGE_FORM_SELECTIONS_KEY, {
		therapist: null,
		seriesTherapists: null,
	});

	const [visitResetNonce, setVisitResetNonce] = useState(0);
	const bumpVisitReset = useCallback(
		() => setVisitResetNonce((nonce) => nonce + 1),
		[],
	);

	const [mitraReasonTarget, setMitraReasonTarget] = useState<number | null>(
		null,
	);

	const [isVisitLocationRevealed, setIsVisitLocationRevealed] = useState(false);
	const revealVisitLocation = useCallback(
		() => setIsVisitLocationRevealed(true),
		[],
	);

	const { t: tReset } = useTranslation("appointments-form", {
		keyPrefix: "reset_dialog",
	});
	const { t: tButton } = useTranslation("appointments-form", {
		keyPrefix: "button",
	});
	const [pendingConfirm, setPendingConfirm] = useState<{
		scope: ResetScope;
		onConfirm: () => void;
	} | null>(null);
	const confirmReset = useCallback(
		(params: { scope: ResetScope; onConfirm: () => void }) =>
			setPendingConfirm(params),
		[],
	);

	return (
		<FormProviderContext.Provider
			value={{
				mode: formMode,
				isSuccessBooked,
				formStorage,
				setFormStorage,
				formSelections,
				setFormSelections,
				isVisitLocationRevealed,
				revealVisitLocation,
				visitResetNonce,
				bumpVisitReset,
				confirmReset,
				mitraReasonTarget,
				setMitraReasonTarget,
			}}
		>
			{children}

			<AlertDialog
				open={!!pendingConfirm}
				onOpenChange={(open) => {
					if (!open) setPendingConfirm(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{tReset("related.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{pendingConfirm &&
								tReset(`related.description.${pendingConfirm.scope}`)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{tButton("cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								const pending = pendingConfirm;
								setPendingConfirm(null);
								pending?.onConfirm();
							}}
						>
							{tButton("continue")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</FormProviderContext.Provider>
	);
}

export const useFormProvider = () => {
	const context = useContext(FormProviderContext);

	if (context === undefined)
		throw new Error(
			"useFormProvider must be used within a FormProviderContext",
		);

	return context;
};

/**
 * Shared reset helpers used by the patient step (patient/location/address change)
 * and the scheduling step. Clearing a visit's date/therapist + bumping the reset
 * nonce remounts the schedule pickers so their internally-held date clears too.
 */
export const useDownstreamReset = () => {
	const form = useFormContext<AppointmentBookingSchema>();
	const { bumpVisitReset } = useFormProvider();
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	// The confirm-and-cascade-reset behaviour is part of the new schedule picker
	// flow. When the flag is off the legacy picker is used, so these guards must
	// stay dormant to preserve the old behaviour (no confirmation, no reset).
	const useNewSchedulePicker = getUseNewSchedulePicker(globalProps);

	const hasScheduledVisits = useCallback(() => {
		if (!useNewSchedulePicker) return false;
		const scheduling = form.getValues("appointmentScheduling");
		if (scheduling?.appointmentDateTime) return true;
		return (scheduling?.seriesVisits ?? []).some(
			(visit) => !!visit?.appointmentDateTime,
		);
	}, [form, useNewSchedulePicker]);

	const hasServicePackageOrVisits = useCallback(() => {
		if (!useNewSchedulePicker) return false;
		const scheduling = form.getValues("appointmentScheduling");
		return (
			!!scheduling?.service?.id ||
			!!scheduling?.package?.id ||
			hasScheduledVisits()
		);
	}, [form, hasScheduledVisits, useNewSchedulePicker]);

	const resetVisits = useCallback(() => {
		form.setValue(
			"appointmentScheduling.therapist",
			{},
			{
				shouldValidate: false,
			},
		);
		form.setValue("appointmentScheduling.appointmentDateTime", null, {
			shouldValidate: false,
		});
		const series = form.getValues("appointmentScheduling.seriesVisits") ?? [];
		series.forEach((_, index) => {
			form.setValue(
				`appointmentScheduling.seriesVisits.${index}.therapist`,
				{},
				{ shouldValidate: false },
			);
			form.setValue(
				`appointmentScheduling.seriesVisits.${index}.appointmentDateTime`,
				null,
				{ shouldValidate: false },
			);
		});
		bumpVisitReset();
	}, [form, bumpVisitReset]);

	const resetServicePackageVisits = useCallback(() => {
		form.setValue("appointmentScheduling.service", DEFAULT_VALUES_SERVICE, {
			shouldValidate: false,
		});
		form.setValue("appointmentScheduling.package", DEFAULT_VALUES_PACKAGE, {
			shouldValidate: false,
		});
		resetVisits();
	}, [form, resetVisits]);

	return {
		hasScheduledVisits,
		hasServicePackageOrVisits,
		resetVisits,
		resetServicePackageVisits,
	};
};

// * component form container
export interface FormContainerProps extends ComponentProps<"div"> {}

export function FormContainer({ className, children }: FormContainerProps) {
	return <div className={cn("grid gap-4", className)}>{children}</div>;
}

// * component form step item container
interface FormStepItemContainerProps extends ComponentProps<"div"> {}

export function FormStepItemContainer({
	className,
	children,
}: FormStepItemContainerProps) {
	return (
		<div
			className={cn(
				"grid grid-cols-1 gap-4 p-4 border shadow-inner md:grid-cols-2 md:p-6 border-border rounded-xl bg-background",
				className,
			)}
		>
			{children}
		</div>
	);
}

// * component step button for next step, prev step, and submit
export interface StepButtonsProps extends ComponentProps<"div"> {
	isFormLoading: boolean;
}

export function StepButtons({ className, isFormLoading }: StepButtonsProps) {
	const {
		isDekstop,
		isDisabledStep,
		isLoading,
		isFirstStep,
		isLastStep,
		isOpenTherapistAlert,
		isOptionalStep,
		onSubmit,
		onPrevStep,
		onBack,
		setIsOpenTherapistAlert,
		blockingSteps,
		onGoToStep,
	} = useStepButtons();
	const { t: taf } = useTranslation("appointments-form");
	const { mode, setMitraReasonTarget } = useFormProvider();
	const bookingForm = useFormContext<AppointmentBookingSchema>();
	const visitCount = useWatch({
		control: bookingForm.control,
		name: "appointmentScheduling.package.numberOfVisit",
	});

	// contents, not grid: the bar below is sticky, and sticky only moves inside a
	// parent taller than itself — this wrapper was exactly the bar's height.
	return (
		<div className={cn("contents", className)}>
			{/* Sits directly above the buttons, on every step, so whatever refuses to
			    advance is read right where the refusal happens. Each step is a button
			    rather than an automatic jump: the admin decides when to leave this
			    screen. */}
			{!!blockingSteps.length && (
				<Alert variant="destructive">
					<AlertCircle className="size-4" />
					<AlertTitle className="text-xs">
						{taf("blocked.title", {
							defaultValue: "Some details still need fixing",
						})}
					</AlertTitle>
					<AlertDescription className="text-xs">
						<ul className="grid gap-2">
							{blockingSteps.map(({ step, label, messages }) => (
								<li key={`${step}-${label}`} className="grid gap-1">
									{/* step -1 means no field could be pinned to a step, so
									    there is nowhere to send the admin — show the message
									    on its own rather than a button that goes nowhere. */}
									{step >= 0 && (
										<Button
											type="button"
											variant="link"
											effect="expandIcon"
											iconPlacement="left"
											className="justify-start h-auto p-0 font-medium w-fit text-inherit"
											icon={Pencil}
											onClick={(event) => {
												event.preventDefault();
												onGoToStep(step);
											}}
										>
											{taf("blocked.go_to_step", {
												defaultValue: "Fix on {{step}}",
												step: label,
											})}
										</Button>
									)}

									<ul className="list-disc list-inside">
										{messages.map(({ text, visitIndex }) => (
											<li key={text}>
												{visitIndex === undefined ? (
													text
												) : (
													<button
														type="button"
														className="underline underline-offset-2 text-left"
														onClick={() => {
															onGoToStep(step);
															setMitraReasonTarget(visitIndex);
														}}
													>
														{text}
													</button>
												)}
											</li>
										))}
									</ul>
								</li>
							))}
						</ul>
					</AlertDescription>
				</Alert>
			)}
			<div className="sticky bottom-0 z-20 flex flex-wrap w-full gap-2 border-t border-border bg-background p-3 [&>button]:grow md:static md:flex-nowrap md:border md:p-6 md:shadow-inner md:rounded-xl md:[&>button]:grow-0">
				<Button
					type="button"
					size={!isDekstop ? "default" : "sm"}
					variant="ghost"
					disabled={
						(isDisabledStep && !isFirstStep) || isLoading || isFormLoading
					}
					onClick={(event) => {
						event.preventDefault();
						isFirstStep ? onBack() : onPrevStep();
					}}
				>
					{isFirstStep ? taf("button.cancel") : taf("button.prev")}
				</Button>

				{isLastStep ? (
					<Button
						type="submit"
						size={!isDekstop ? "default" : "sm"}
						disabled={isLoading || isFormLoading}
					>
						{isLoading || isFormLoading ? (
							<>
								<LoaderIcon className="animate-spin" />
								<span>{taf("button.save.loading")}</span>
							</>
						) : (
							<span>
								{mode === "new" && visitCount
									? taf("button.create_booking", { count: Number(visitCount) })
									: taf("button.save.label")}
							</span>
						)}
					</Button>
				) : (
					<Button
						type="button"
						size={!isDekstop ? "default" : "sm"}
						disabled={isLoading || isFormLoading}
						onClick={(event) => {
							event.preventDefault();
							onSubmit();
						}}
					>
						{isLoading || isFormLoading ? (
							<>
								<LoaderIcon className="animate-spin" />
								<span>{taf("button.next.loading")}</span>
							</>
						) : isOptionalStep ? (
							<span>{taf("button.skip")}</span>
						) : (
							<span>{taf("button.next.label")}</span>
						)}
					</Button>
				)}

				<AlertDialog
					open={isOpenTherapistAlert}
					onOpenChange={setIsOpenTherapistAlert}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{taf("modal.title")}</AlertDialogTitle>
							<AlertDialogDescription>
								{taf("modal.description")}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter className="space-y-4 md:space-y-0">
							<AlertDialogCancel>
								{taf("modal.button.cancel")}
							</AlertDialogCancel>
							<AlertDialogAction asChild>
								<Button
									type="button"
									size={!isDekstop ? "default" : "sm"}
									disabled={isLoading || isFormLoading}
									className="order-first md:order-last"
									onClick={(event) => {
										event.preventDefault();
										onSubmit({ skipTherapist: true });
									}}
								>
									{isLoading || isFormLoading ? (
										<>
											<LoaderIcon className="animate-spin" />
											<span>{taf("modal.button.next.loading")}</span>
										</>
									) : isOptionalStep ? (
										<span>{taf("modal.button.skip")}</span>
									) : (
										<span>{taf("modal.button.next.label")}</span>
									)}
								</Button>
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	);
}

export function FinalStep() {
	const { t: taf } = useTranslation("appointments-form", {
		keyPrefix: "final_step",
	});
	const { count, hasCompletedAllSteps, redirectURL } = useFinalStep();

	if (!hasCompletedAllSteps) return null;

	return (
		<FormStepItemContainer
			className={cn(
				"!grid-cols-1 !gap-2 mt-0 text-center md:mt-4 relative",
				count === 0 && "motion-preset-confetti",
			)}
		>
			<div className="flex flex-col gap-3">
				<CircleCheckBig className="mx-auto size-8 text-primary" />
				<h1 className="mx-auto space-x-3 text-sm font-bold text-primary">
					<span>{taf("title")}</span>
				</h1>

				<p className="text-xs text-pretty w-full md:w-[75%] mx-auto text-primary">
					{taf("description")}
				</p>

				<span className="my-2 text-3xl font-bold text-primary">{count}</span>

				<Button effect="shine" size="lg" asChild>
					<Link href={redirectURL}>{taf("button")}</Link>
				</Button>
			</div>

			<RetroGridPattern />
		</FormStepItemContainer>
	);
}

export const PatientDetailsForm = memo(function Component() {
	const { isExistingPatientSource } = usePatientDetailsForm();

	return (
		<FormStepItemContainer>
			<Suspense
				fallback={
					<div className="col-span-full grid grid-cols-2 items-center gap-4">
						{Array.from({ length: 2 }).map((_, index) => (
							<Skeleton
								key={String(index)}
								className="w-full h-12 rounded-md shadow-inner border-input"
							/>
						))}

						<Skeleton className="w-full h-20 rounded-md shadow-inner border-input col-span-full" />
					</div>
				}
			>
				<ExistingPatientSelection />
			</Suspense>

			<Separator className="mt-3 col-span-full" />

			{isExistingPatientSource ? (
				<Suspense
					fallback={
						<div className="col-span-full grid items-center gap-4">
							<div className="flex items-center justify-between gap-4">
								<Skeleton className="w-[200px] h-6 rounded-md shadow-inner border-input" />
								<Skeleton className="w-12 h-6 rounded-md shadow-inner border-input" />
							</div>

							<Skeleton className="w-full h-20 rounded-md shadow-inner border-input col-span-full" />
						</div>
					}
				>
					<PatientMedicalForm />
				</Suspense>
			) : (
				<Fragment>
					<Suspense
						fallback={
							<div className="col-span-full grid items-center gap-4">
								<div className="flex items-center justify-between gap-4">
									<Skeleton className="w-[200px] h-6 rounded-md shadow-inner border-input" />
									<Skeleton className="w-12 h-6 rounded-md shadow-inner border-input" />
								</div>

								<Skeleton className="w-full h-20 rounded-md shadow-inner border-input col-span-full" />
							</div>
						}
					>
						<PatientContactForm />
					</Suspense>

					<Separator className="mt-3 col-span-full" />

					<Suspense
						fallback={
							<div className="col-span-full grid items-center gap-4">
								<div className="flex items-center justify-between gap-4">
									<Skeleton className="w-[200px] h-6 rounded-md shadow-inner border-input" />
									<Skeleton className="w-12 h-6 rounded-md shadow-inner border-input" />
								</div>

								<Skeleton className="w-full h-20 rounded-md shadow-inner border-input col-span-full" />
							</div>
						}
					>
						<PatientBasicInfoForm />
					</Suspense>

					<Separator className="mt-3 col-span-full" />

					<Suspense
						fallback={
							<div className="col-span-full grid items-center gap-4">
								<div className="flex items-center justify-between gap-4">
									<Skeleton className="w-[200px] h-6 rounded-md shadow-inner border-input" />
									<Skeleton className="w-12 h-6 rounded-md shadow-inner border-input" />
								</div>

								<Skeleton className="w-full h-20 rounded-md shadow-inner border-input col-span-full" />
							</div>
						}
					>
						<PatientMedicalForm />
					</Suspense>
				</Fragment>
			)}

			<Separator className="mt-3 col-span-full" />

			<BookingSourceForm />
		</FormStepItemContainer>
	);
});

export function AppointmentSchedulingForm() {
	const { t: tas } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule",
	});
	const { t: tasf } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.fields",
	});

	const { locale, tzDate } = useDateContext();
	const isFirstRender = useIsFirstRender();
	const {
		mode,
		formSelections,
		visitResetNonce,
		bumpVisitReset,
		isVisitLocationRevealed,
		revealVisitLocation,
	} = useFormProvider();
	// An existing patient carries their own address, so only a new patient is
	// asked for the visit location here.
	const { isExistingPatientSource } = usePatientDetailsForm();

	// Get feature flag from page props
	const { props } = usePage<AppointmentNewGlobalPageProps>();
	const useNewSchedulePicker = getUseNewSchedulePicker(props);
	const [scheduleMode, setScheduleMode] = useState<ScheduleMode>("picker");
	const { scheduleDraftSave } = useDraftAutosave();

	// For scheduling-first approach, location selection will be added to the form
	const schedulingFormResult = useAppointmentSchedulingForm();
	const {
		form,
		isLoading,
		watchAppointmentSchedulingValue,
		watchAllOfDayValue,
		onSelectAllOfDay: _onSelectAllOfDay,
		onDateTimeChange,
		...restSchedulingHooks
	} = schedulingFormResult;

	// Get patient details separately to access location
	const watchPatientDetailsValue = useWatch({
		control: form.control,
		name: "patientDetails",
	});

	// An existing patient normally carries their own address, so they are not asked
	// for the visit location. When the record they arrived with is missing part of
	// it, though, this step's validation blocks on fields no form renders — so the
	// location form appears for them too, and stays for the rest of the booking:
	// completing it must not make the form the admin is working in disappear. The
	// booking POST upserts the address it receives, so the fix reaches the record.
	const hasIncompleteStoredLocation = !hasCompleteVisitLocation(
		watchPatientDetailsValue,
	);
	useEffect(() => {
		if (hasIncompleteStoredLocation) revealVisitLocation();
	}, [hasIncompleteStoredLocation, revealVisitLocation]);
	const showVisitLocationForm =
		!isExistingPatientSource ||
		hasIncompleteStoredLocation ||
		isVisitLocationRevealed;

	// Phase D schedule picker (visit 1) — map a SlotSelection onto the form fields.
	const visit1Selection = useMemo<SlotSelection | null>(() => {
		const therapist = watchAppointmentSchedulingValue?.therapist;
		const dateTime = watchAppointmentSchedulingValue?.appointmentDateTime;
		if (!therapist?.id || !dateTime) return null;
		const d = new Date(dateTime);
		return {
			therapistId: String(therapist.id),
			therapistName: therapist.name ?? "",
			therapistType: therapist.employmentType
				? employmentTypeToTherapistType(therapist.employmentType)
				: "internal",
			date: toIsoDate(d),
			start_time: format(d, "HH:mm"),
			end_time: "",
		};
	}, [watchAppointmentSchedulingValue]);
	// Dates already taken by the later series visits (2..N), struck through in
	// visit 1's calendar so the admin sees they're used elsewhere.
	const visit1OtherScheduledDates = useMemo(() => {
		const dates: string[] = [];
		for (const visit of watchAppointmentSchedulingValue?.seriesVisits ?? []) {
			if (visit?.appointmentDateTime) {
				dates.push(toIsoDate(new Date(visit.appointmentDateTime)));
			}
		}
		return dates;
	}, [watchAppointmentSchedulingValue]);
	const applyVisit1Selection = useCallback(
		(selection: SlotSelection | null) => {
			if (selection) {
				form.setValue(
					"appointmentScheduling.therapist",
					slotSelectionToTherapist(selection),
					{ shouldValidate: true },
				);
				form.setValue(
					"appointmentScheduling.appointmentDateTime",
					slotSelectionToDateTime(selection),
					{ shouldValidate: true },
				);
			} else {
				form.setValue(
					"appointmentScheduling.therapist",
					{},
					{ shouldValidate: false },
				);
				form.setValue("appointmentScheduling.appointmentDateTime", null, {
					shouldValidate: false,
				});
			}
			// Persist every change (debounced + deduped by the shared autosave). The
			// picker now only emits a null on an explicit "Hapus pilihan" — filter/
			// date/mode no longer clear — so a null here is a deliberate change.
			scheduleDraftSave();
		},
		[form, scheduleDraftSave],
	);
	// The visit dates the patient named in the WhatsApp form. They survive the round trip
	// because the draft is restored with form.reset(formData) and autosaved with
	// form.getValues() — keys the schema does not declare are carried, not dropped.
	const patientPreferredSchedules = useMemo(
		() => preferredSchedules(watchAppointmentSchedulingValue),
		[watchAppointmentSchedulingValue],
	);

	const { preferredTherapistGenderOption } = restSchedulingHooks;
	const {
		alertService,
		servicesOption,
		packagesOption,
		onFocusServiceField,
		onSelectService,
		onSelectPackage,
	} = restSchedulingHooks;
	const {
		mapRef,
		isIsolineCalculated,
		markerStorage,
		isolineStorage,
		generateMarkerDataTherapist,
	} = restSchedulingHooks;
	const {
		isTherapistFound,
		therapistsOptions,
		onSelectTherapist,
		onResetAllTherapistState,
	} = restSchedulingHooks;

	// Changing gender/service/package invalidates already-scheduled visits.
	// Guard those changes with a confirmation that resets ALL visits.
	const { t: tReset } = useTranslation("appointments-form", {
		keyPrefix: "reset_dialog",
	});
	const { t: tButton } = useTranslation("appointments-form", {
		keyPrefix: "button",
	});
	const [pendingVisitReset, setPendingVisitReset] = useState<{
		field: VisitResetField;
		apply: () => void;
	} | null>(null);

	const hasScheduledVisits = useCallback(() => {
		// Guarded reset (gender/service/package) is part of the new picker flow only;
		// keep it dormant under the legacy picker so the old behaviour is preserved.
		if (!useNewSchedulePicker) return false;
		const scheduling = form.getValues("appointmentScheduling");
		if (scheduling?.appointmentDateTime) return true;
		return (scheduling?.seriesVisits ?? []).some(
			(visit) => !!visit?.appointmentDateTime,
		);
	}, [form, useNewSchedulePicker]);

	const resetAllVisits = useCallback(() => {
		form.setValue(
			"appointmentScheduling.therapist",
			{},
			{ shouldValidate: false },
		);
		form.setValue("appointmentScheduling.appointmentDateTime", null, {
			shouldValidate: false,
		});
		const series = form.getValues("appointmentScheduling.seriesVisits") ?? [];
		series.forEach((_, index) => {
			form.setValue(
				`appointmentScheduling.seriesVisits.${index}.therapist`,
				{},
				{ shouldValidate: false },
			);
			form.setValue(
				`appointmentScheduling.seriesVisits.${index}.appointmentDateTime`,
				null,
				{ shouldValidate: false },
			);
		});
		onResetAllTherapistState();
		// Remount the schedule pickers so their internally-held date clears too.
		bumpVisitReset();
	}, [form, onResetAllTherapistState, bumpVisitReset]);

	// If visits are already scheduled, confirm before applying the change;
	// otherwise apply immediately.
	const guardVisitReset = useCallback(
		(field: VisitResetField, apply: () => void) => {
			if (hasScheduledVisits()) {
				setPendingVisitReset({ field, apply });
			} else {
				apply();
			}
		},
		[hasScheduledVisits],
	);

	// Changing visit 1's therapist invalidates the continuity of the series
	// visits, so (on confirm inside the picker) reset every series visit.
	const scheduledSeriesVisitCount = (
		watchAppointmentSchedulingValue?.seriesVisits ?? []
	).filter((visit) => !!visit?.appointmentDateTime).length;
	const resetSeriesVisitsFrom = useCallback(
		(startIndex: number) => {
			const series = form.getValues("appointmentScheduling.seriesVisits") ?? [];
			series.forEach((_, index) => {
				if (index >= startIndex) {
					form.setValue(
						`appointmentScheduling.seriesVisits.${index}.therapist`,
						{},
						{ shouldValidate: false },
					);
					form.setValue(
						`appointmentScheduling.seriesVisits.${index}.appointmentDateTime`,
						null,
						{ shouldValidate: false },
					);
				}
			});
			bumpVisitReset();
		},
		[form, bumpVisitReset],
	);

	// The legacy therapist selector (TherapistSearchList) doesn't go through the
	// schedule picker's own confirm+reset guard — wrap it here so a real therapist
	// change with later scheduled visits still asks before resetting those visits
	// (mirrors the picker's behaviour).
	const [pendingLegacyTherapist, setPendingLegacyTherapist] = useState<
		AppointmentBookingSchema["appointmentScheduling"]["therapist"] | null
	>(null);
	const handleLegacySelectTherapist = useCallback(
		(
			therapist: AppointmentBookingSchema["appointmentScheduling"]["therapist"],
		) => {
			const committedId = form.getValues("appointmentScheduling.therapist.id");
			const hasBaselineTherapist = !!committedId;
			if (
				hasBaselineTherapist &&
				shouldConfirmLaterReset({
					committedId: committedId ? String(committedId) : null,
					nextId: therapist?.id ? String(therapist.id) : null,
					hasLaterVisits: scheduledSeriesVisitCount > 0,
				})
			) {
				setPendingLegacyTherapist(therapist);
				return;
			}
			onSelectTherapist(therapist);
		},
		[form, scheduledSeriesVisitCount, onSelectTherapist],
	);

	// get the badge statuses
	const getStatusBadge = useMemo(() => {
		const isTimed = hasVisitTime(
			toValidDate(watchAppointmentSchedulingValue?.appointmentDateTime),
		);
		const className = !isTimed
			? getBadgeVariantStatus("unscheduled")
			: watchAppointmentSchedulingValue?.therapist?.id
				? getBadgeVariantStatus("scheduled")
				: getBadgeVariantStatus("pending_therapist_assignment");
		const label = !isTimed
			? tas("visit_status.unscheduled")
			: watchAppointmentSchedulingValue?.therapist?.id
				? tas("visit_status.scheduled")
				: tas("visit_status.pending_therapist");

		return { className, label };
	}, [watchAppointmentSchedulingValue, tas]);

	const [isOpenAccordion, setIsOpenAccordion] = useState<string>("first-visit");
	const onCloseAccordionItem = useCallback(() => {
		setIsOpenAccordion("");
	}, []);

	// * side effect to add isoline calculated and stored isoline and the marker marked to the map
	useEffect(() => {
		// Ensure the map is initialized and isolane not calculated, therapist selected not changed, and restrict to the first render
		if (
			!mapRef?.current ||
			isIsolineCalculated ||
			(watchAppointmentSchedulingValue?.therapist?.id !==
				formSelections?.therapist?.id &&
				(!isFirstRender || !isOpenAccordion))
		)
			return;

		// Add previously stored isolines to the map, if available
		if (isolineStorage) {
			mapRef.current.isoline.onAddAll(isolineStorage);
		}

		// Add previously stored patient markers to the map, if available
		if (markerStorage) {
			mapRef.current.marker.onAdd(markerStorage.patient);
		}

		// Check if therapist information is selected and add therapist marker
		if (formSelections.therapist) {
			// Prepare therapist marker data with fallback defaults
			const therapistMarkerData = generateMarkerDataTherapist({
				address: formSelections.therapist.activeAddress?.address || "",
				position: {
					lat: formSelections.therapist.activeAddress?.latitude || 0,
					lng: formSelections.therapist.activeAddress?.longitude || 0,
				},
				therapist: formSelections.therapist,
			});
			// Add therapist marker to the map as a secondary marker with routing enabled
			mapRef.current.marker.onAdd([therapistMarkerData], {
				isSecondary: true,
				useRouting: true,
			});
		}
	}, [
		mapRef?.current,
		isolineStorage,
		isIsolineCalculated,
		markerStorage,
		formSelections.therapist,
		watchAppointmentSchedulingValue?.therapist,
		isFirstRender,
		isOpenAccordion,
		generateMarkerDataTherapist,
	]);

	const legacyScheduleFields = (
		<Fragment key={`visit1-legacy-${visitResetNonce}`}>
			{watchAppointmentSchedulingValue?.therapist?.name ? (
				<SelectedTherapistCard
					className="col-span-full"
					therapist={watchAppointmentSchedulingValue.therapist}
					onUnselect={() => handleLegacySelectTherapist(null)}
				/>
			) : null}

			<FormField
				control={form.control}
				name="appointmentScheduling.appointmentDateTime"
				render={({ field }) => (
					<FormItem className="col-span-full">
						<FormControl>
							<DateTimePicker
								value={field.value}
								timeVariant="select"
								onChangeValue={field.onChange}
								isAllOfDay={!!watchAllOfDayValue}
								autoScroll={false}
								callbackOnChange={async () => {
									// Therapist reset is now handled by useEffect in useTherapistAvailability
									// when date becomes null - no manual reset needed here
									// auto-save draft when date/time changes
									await onDateTimeChange();
								}}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<TherapistSearchList
				className="col-span-full"
				instanceId="visit-1"
				locationId={watchPatientDetailsValue?.location?.id?.toString()}
				origin={{
					city: watchPatientDetailsValue?.location?.city,
					latitude: watchPatientDetailsValue?.latitude,
					longitude: watchPatientDetailsValue?.longitude,
				}}
				serviceId={watchAppointmentSchedulingValue?.service?.id?.toString()}
				appointmentDateTime={
					watchAppointmentSchedulingValue?.appointmentDateTime
				}
				preferredTherapistGender={
					watchAppointmentSchedulingValue?.preferredTherapistGender
				}
				isAllOfDay={!!watchAllOfDayValue}
				selectedTherapistId={
					watchAppointmentSchedulingValue?.therapist?.id ?? null
				}
				onSelect={(therapist) => handleLegacySelectTherapist(therapist)}
			/>
		</Fragment>
	);

	return (
		<FormStepItemContainer>
			<AlertDialog
				open={!!pendingVisitReset}
				onOpenChange={(open) => {
					if (!open) setPendingVisitReset(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{tReset("visits.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{pendingVisitReset &&
								tReset(`visits.description.${pendingVisitReset.field}`)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{tButton("cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								const pending = pendingVisitReset;
								setPendingVisitReset(null);
								resetAllVisits();
								pending?.apply();
							}}
						>
							{tButton("continue")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={!!pendingLegacyTherapist}
				onOpenChange={(open) => {
					if (!open) setPendingLegacyTherapist(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{tReset("therapist.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{tReset("therapist.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{tButton("cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (pendingLegacyTherapist) {
									resetSeriesVisitsFrom(0);
									onSelectTherapist(pendingLegacyTherapist);
								}
								setPendingLegacyTherapist(null);
							}}
						>
							{tButton("continue")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{showVisitLocationForm && (
				<Fragment>
					<PatientRegionForm />

					<Separator className="mt-3 col-span-full" />
				</Fragment>
			)}

			{mode === "series" && (
				<div className="p-3 text-sm border rounded-md shadow-inner border-input bg-sidebar col-span-full">
					<div className="flex items-center gap-3">
						<Avatar className="border rounded-lg border-black/10 bg-muted size-12">
							<AvatarImage src="#" />
							<AvatarFallback>
								<Hospital className="flex-shrink-0 size-5 text-muted-foreground/75" />
							</AvatarFallback>
						</Avatar>

						<div className="grid text-sm line-clamp-1">
							<p className="font-semibold uppercase truncate">
								{watchAppointmentSchedulingValue.service.name.replaceAll(
									"_",
									" ",
								)}
							</p>

							<div className="flex items-center gap-1 mt-2 text-xs">
								<p className="font-light text-pretty">
									<span className="uppercase">
										{watchAppointmentSchedulingValue.package.name}
									</span>
									<span className="mx-2">&#x2022;</span>
									<span className="italic font-light">
										{watchAppointmentSchedulingValue.package.numberOfVisit ||
											"N/A"}{" "}
										visit(s)
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			)}

			{(alertService !== null ||
				(!therapistsOptions?.feasible?.length && isTherapistFound)) && (
				<Alert
					variant="destructive"
					className="col-span-full motion-preset-rebound-down"
				>
					<AlertCircle className="w-4 h-4" />
					<AlertTitle>
						{alertService?.title || tas("alert_therapist.title")}
					</AlertTitle>
					<AlertDescription>
						{alertService?.description || tas("alert_therapist.description")}
					</AlertDescription>
				</Alert>
			)}

			<p className="mb-2 text-xs tracking-wider uppercase text-muted-foreground col-span-full">
				{tas("service_schedule_label")}
			</p>

			{mode === "new" && (
				<>
					<FormField
						control={form.control}
						name="appointmentScheduling.service.name"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-nowrap">
									{tasf("service.label")}
								</FormLabel>
								<Popover>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												variant="outline"
												className={cn(
													"relative w-full flex justify-between font-normal bg-sidebar shadow-inner",
													!field.value && "text-muted-foreground",
												)}
												onFocus={() => onFocusServiceField()}
											>
												<p>
													{field.value
														? servicesOption
																?.find(
																	(service) => service.name === field.value,
																)
																?.name?.replaceAll("_", " ") ||
															field.value.replaceAll("_", " ")
														: tasf("service.placeholder")}
												</p>
												{field.value ? (
													// biome-ignore lint/a11y/useSemanticElements: act like a button
													<div
														role="button"
														tabIndex={0}
														className="cursor-pointer"
														onKeyDown={() => {}}
														onClick={(event) => {
															event.preventDefault();
															event.stopPropagation();

															guardVisitReset("service", () =>
																onSelectService(DEFAULT_VALUES_SERVICE),
															);
														}}
													>
														<X className="opacity-50" />
													</div>
												) : (
													<ChevronsUpDown className="opacity-50" />
												)}
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent
										className="p-0 w-[300px]"
										align="start"
										side="bottom"
									>
										<Command>
											<CommandInput
												placeholder={tasf("service.search.placeholder")}
												className="h-9"
												disabled={isLoading.services}
											/>
											<CommandList>
												<CommandEmpty>
													{tasf("service.search.empty")}
												</CommandEmpty>
												<CommandGroup>
													{isLoading.services ? (
														<CommandItem value={undefined} disabled>
															<LoaderIcon className="animate-spin" />
															<span>{tasf("service.search.loading")}</span>
														</CommandItem>
													) : (
														servicesOption?.map((service) => (
															<CommandItem
																key={service.id}
																value={service.name}
																onSelect={() =>
																	guardVisitReset("service", () =>
																		onSelectService({
																			id: String(service.id),
																			name: service.name,
																		}),
																	)
																}
															>
																<span className="flex flex-col items-start">
																	<span>
																		{service.name.replaceAll("_", " ")}
																	</span>
																	<span className="text-xs font-light text-pretty">
																		{service.description}
																	</span>
																</span>

																<Check
																	className={cn(
																		"ml-auto",
																		service.name === field.value
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
															</CommandItem>
														))
													)}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="appointmentScheduling.package.name"
						render={({ field }) => {
							const selectedPackage = packagesOption?.find(
								(packageItem) => packageItem.name === field.value,
							);

							return (
								<FormItem>
									<FormLabel className="text-nowrap">
										{tasf("package.label")}
									</FormLabel>
									<Popover>
										<PopoverTrigger asChild>
											<FormControl>
												<Button
													variant="outline"
													className={cn(
														"relative w-full flex justify-between font-normal bg-sidebar shadow-inner",
														!field.value && "text-muted-foreground",
													)}
												>
													{field.value ? (
														<p className="flex min-w-0 items-baseline gap-2">
															<span className="min-w-0 truncate">
																<span>
																	{selectedPackage?.name || field.value}
																</span>{" "}
																<span className="italic font-light">
																	(
																	{tas("package_visits", {
																		count: Number(
																			selectedPackage?.numberOfVisit ||
																				watchAppointmentSchedulingValue?.package
																					?.numberOfVisit,
																		),
																	})}
																	)
																</span>
															</span>
															{selectedPackage?.totalPrice && (
																<span className="shrink-0 tabular-nums text-muted-foreground">
																	{formatCurrency(selectedPackage.totalPrice, {
																		currency: selectedPackage.currency || "IDR",
																	})}
																</span>
															)}
														</p>
													) : (
														<span>{tasf("package.placeholder")}</span>
													)}
													<ChevronsUpDown className="opacity-50" />
												</Button>
											</FormControl>
										</PopoverTrigger>
										<PopoverContent
											className="p-0 w-[300px]"
											align="start"
											side="bottom"
										>
											<Command>
												<CommandInput
													placeholder={tasf("package.search.placeholder")}
													className="h-9"
												/>
												<CommandList>
													<CommandEmpty>
														{tasf("package.search.empty")}
													</CommandEmpty>
													<CommandGroup>
														{packagesOption?.map((packageItem) => (
															<CommandItem
																key={packageItem.id}
																value={packageItem.name}
																onSelect={() =>
																	guardVisitReset("package", () =>
																		onSelectPackage({
																			id: packageItem.id,
																			name: packageItem.name,
																			numberOfVisit: packageItem.numberOfVisit,
																		}),
																	)
																}
															>
																<p>
																	<span>{packageItem.name}</span>{" "}
																	<span className="italic font-light">
																		(
																		{tas("package_visits", {
																			count: Number(packageItem.numberOfVisit),
																		})}
																		)
																	</span>
																</p>
																<span className="ml-auto pl-3 tabular-nums text-muted-foreground">
																	{formatCurrency(packageItem.totalPrice, {
																		currency: packageItem.currency || "IDR",
																	})}
																</span>

																<Check
																	className={cn(
																		"ml-2",
																		packageItem.name === field.value
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
															</CommandItem>
														))}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>

									<FormMessage />
								</FormItem>
							);
						}}
					/>

					<Deferred
						data={["optionsData"]}
						fallback={
							<div className="flex flex-col self-end gap-3 col-span-full">
								<Skeleton className="w-10 h-4 rounded-md" />
								<div className="grid grid-cols-1 gap-4">
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
								</div>
							</div>
						}
					>
						<FormField
							control={form.control}
							name="appointmentScheduling.preferredTherapistGender"
							render={({ field }) => (
								<FormItem className="space-y-3 col-span-full">
									<FormLabel>{tasf("pref_therapist_gender.label")}</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={(value) => {
												guardVisitReset("gender", () => {
													field.onChange(value);
													onResetAllTherapistState();
												});
											}}
											value={field.value}
											orientation="horizontal"
											className="grid grid-cols-1 gap-4 md:grid-cols-3"
										>
											{preferredTherapistGenderOption.map((gender) => (
												<FormItem
													key={gender}
													className="flex items-start p-3 space-x-3 space-y-0 border rounded-md shadow-inner border-input bg-sidebar"
												>
													<FormControl>
														<RadioGroupItem value={gender} />
													</FormControl>
													<FormLabel className="flex items-center gap-1 font-normal uppercase">
														{getGenderIcon(gender)}
														<span>
															{tasf(
																`pref_therapist_gender.options.${gender.toLowerCase()}`,
															)}
														</span>
													</FormLabel>
												</FormItem>
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</Deferred>

					{/* Sits with the booking's preferences, not inside Visit 1: these are
					    what the patient asked for, not a Visit anybody has planned. The
					    admin still picks the real date on the calendar below. */}
					<PreferredSchedules
						className="col-span-full"
						schedules={patientPreferredSchedules}
					/>
				</>
			)}

			{watchAppointmentSchedulingValue?.service?.id &&
				watchAppointmentSchedulingValue?.package?.id && (
					<div className="col-span-full grid grid-cols-1 gap-4 xl:grid-cols-2">
						<Accordion
							collapsible
							type="single"
							value={isOpenAccordion}
							onValueChange={setIsOpenAccordion}
							className="w-full -mb-2"
						>
							<AccordionItem
								value="first-visit"
								className="bg-sidebar text-muted-foreground border border-border rounded-md has-focus-visible:border-ring has-focus-visible:ring-ring/50 px-4 py-1 outline-none last:border-b has-focus-visible:ring-[3px]"
							>
								<AccordionPrimitive.Header className="flex">
									<AccordionPrimitive.Trigger className="focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-center justify-between rounded-md py-2 text-left text-base leading-6 font-semibold transition-all outline-none focus-visible:ring-[3px] [&[data-state=open]>svg]:rotate-180">
										<div className="flex gap-3">
											<span
												className="flex size-10 shrink-0 items-center justify-center rounded-full border bg-primary/75 text-primary-foreground"
												aria-hidden="true"
											>
												1
											</span>
											<span className="flex flex-col">
												<span>{tas("visit", { number: 1 })}</span>

												{(() => {
													const validDateTime = toValidDate(
														watchAppointmentSchedulingValue?.appointmentDateTime,
													);
													return (
														<span className="text-sm font-normal">
															{validDateTime
																? format(
																		validDateTime,
																		locale.code === "id"
																			? "d MMMM yyyy, HH.mm"
																			: "MMMM d, yyyy, h:mm a",
																		{ locale, in: tzDate },
																	)
																: tas("visit_status.unscheduled")}
														</span>
													);
												})()}

												{watchAppointmentSchedulingValue?.therapist?.name ? (
													<span className="text-xs text-muted-foreground font-normal uppercase">
														{watchAppointmentSchedulingValue.therapist.name}
													</span>
												) : (
													<span className="text-xs text-muted-foreground font-normal uppercase">
														{locale.code === "id" ? "T/A" : "N/A"}
													</span>
												)}
											</span>
										</div>

										<div className="flex items-center gap-3">
											<Badge
												variant="outline"
												className={cn(
													getStatusBadge.className,
													"uppercase !px-1",
												)}
											>
												{getStatusBadge.label}
											</Badge>

											<ChevronDownIcon
												size={16}
												className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
												aria-hidden="true"
											/>
										</div>
									</AccordionPrimitive.Trigger>
								</AccordionPrimitive.Header>
								<AccordionContent className="text-muted-foreground pb-2">
									<div className="p-4 px-0 pb-0 border-t bg-muted/5 grid grid-cols-1 md:grid-cols-2 gap-4">
										{useNewSchedulePicker ? (
											<>
												<ScheduleModeToggle
													value={scheduleMode}
													onChange={setScheduleMode}
												/>

												{scheduleMode === "picker" ? (
													<div className="col-span-full">
														<SchedulePicker
															key={`visit1-${visitResetNonce}`}
															latitude={Number(
																watchPatientDetailsValue?.latitude,
															)}
															longitude={Number(
																watchPatientDetailsValue?.longitude,
															)}
															locationId={
																watchPatientDetailsValue?.location?.id?.toString() ??
																""
															}
															serviceId={
																watchAppointmentSchedulingValue?.service?.id?.toString() ??
																""
															}
															gender={
																watchAppointmentSchedulingValue?.preferredTherapistGender
															}
															value={visit1Selection}
															onChange={applyVisit1Selection}
															shouldConfirmTherapistChange={
																scheduledSeriesVisitCount > 0
															}
															onTherapistChangeConfirmed={() =>
																resetSeriesVisitsFrom(0)
															}
															scheduledDates={visit1OtherScheduledDates}
														/>
													</div>
												) : (
													legacyScheduleFields
												)}
											</>
										) : (
											legacyScheduleFields
										)}
									</div>

									<div className="mt-4 flex items-center gap-2">
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={onCloseAccordionItem}
										>
											{tButton("close")}
										</Button>
									</div>
								</AccordionContent>
							</AccordionItem>
						</Accordion>

						<SeriesScheduler />
					</div>
				)}

			<MitraAssignmentReasonField />
			<MitraAssignmentReasonSync />
		</FormStepItemContainer>
	);
}

function useMitraReasonEnabled() {
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	return (
		globalProps?.adminPortal?.featureFlags?.mitraAssignmentReasonEnabled ??
		false
	);
}

function MitraAssignmentReasonSync() {
	const form = useFormContext<AppointmentBookingSchema>();
	const scheduling = useWatch({
		control: form.control,
		name: "appointmentScheduling",
	});
	const baseline = useContext(DraftBaselineContext);
	const enabled = useMitraReasonEnabled();

	useEffect(() => {
		if (!enabled) return;

		for (const [index, value] of mitraReasonUpdates(scheduling, baseline)) {
			form.setValue(mitraReasonFieldName(index), value, {
				shouldValidate: true,
			});
		}
	}, [scheduling, baseline, enabled, form]);

	return null;
}

/** One reason for the whole Booking, written to every visit it covers; see mitraReasonUpdates. */
function MitraAssignmentReasonField() {
	const { t: tas } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.fields.mitra_assignment_reason",
	});
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	const form = useFormContext<AppointmentBookingSchema>();
	const scheduling = useWatch({
		control: form.control,
		name: "appointmentScheduling",
	});
	const { errors } = useFormState({ control: form.control });
	const baseline = useContext(DraftBaselineContext);
	const enabled = useMitraReasonEnabled();
	const visits = enabled ? mitraReasonVisits(scheduling, baseline) : [];
	const shown = visits.length > 0;
	const { mitraReasonTarget, setMitraReasonTarget } = useFormProvider();
	const id = "mitra-reason";

	useEffect(() => {
		if (mitraReasonTarget === null) return;
		// A hidden field drops the request, or it would steal focus whenever it next appears.
		if (!shown) return setMitraReasonTarget(null);
		const el = document.getElementById(id);
		if (!el) return;
		el.focus({ preventScroll: true });
		setMitraReasonTarget(null);
		el.scrollIntoView({ block: "center" });
	}, [mitraReasonTarget, shown, setMitraReasonTarget]);

	if (!shown) return null;

	const error = visits
		.map(
			({ index }) =>
				get(errors, mitraReasonFieldName(index))?.message as string | undefined,
		)
		.find(Boolean);

	return (
		<MitraAssignmentReasonBox
			id={id}
			className="col-span-full"
			label={tas("label")}
			description={tas("description")}
			options={globalProps?.mitraAssignmentReasons || []}
			value={
				visits.find((visit) => visit.mitraAssignmentReason)
					?.mitraAssignmentReason
			}
			onChange={(value) => {
				for (const { index } of visits) {
					form.setValue(mitraReasonFieldName(index), value, {
						shouldDirty: true,
						shouldValidate: true,
					});
				}
			}}
			error={error && tas(`errors.${error}`)}
		/>
	);
}

export function AdditionalSettingsForm() {
	const { t } = useTranslation("appointments-form");
	const { t: tpg } = useTranslation("appointments-form", {
		keyPrefix: "payment_gateway",
	});
	const { mode } = useFormProvider();
	const { form, watchAdditionalSettingsValue } = useAdditionalSettingsForm();
	const { selectedPackage, voucherCode, voucher, checkVoucher } =
		useBookingPrice();

	return (
		<FormStepItemContainer>
			{mode === "new" && (
				<>
					<FormField
						control={form.control}
						name="additionalSettings.bypassSlotConflict"
						render={({ field }) => (
							<FormItem className="col-span-full">
								<BypassSlotConflictField
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="additionalSettings.enablePaymentGateway"
						render={({ field }) => (
							<FormItem className="col-span-full">
								<div className="relative flex w-full items-start gap-2 rounded-md border border-input p-4 shadow-xs outline-none bg-card has-data-[state=checked]:border-primary/50">
									<Switch
										id="enable-payment-gateway"
										checked={field.value}
										onCheckedChange={(checked) => {
											field.onChange(checked);
											if (!checked) clearPaymentExpiry(form);
										}}
										aria-describedby="enable-payment-gateway-description"
										className="data-[state=checked]:[&_span]:rtl:-translate-x-2 order-1 h-4 w-6 after:absolute after:inset-0 [&_span]:size-3 data-[state=checked]:[&_span]:translate-x-2"
									/>
									<div className="grid grow gap-2">
										<Label htmlFor="enable-payment-gateway">
											{tpg("enable_label")}
											<span className="font-normal text-muted-foreground text-xs leading-[inherit]">
												{" "}
												({t("common.optional")})
											</span>
										</Label>
										<p
											className="text-muted-foreground text-xs"
											id="enable-payment-gateway-description"
										>
											{tpg("enable_description")}
										</p>
									</div>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					{watchAdditionalSettingsValue?.enablePaymentGateway && (
						<FormField
							control={form.control}
							name="additionalSettings.paymentExpiryMinutes"
							render={({ field }) => (
								<FormItem className="col-span-full motion-preset-rebound-down">
									<FormLabel>{tpg("expiry_label")}</FormLabel>
									<FormControl>
										<div className="flex flex-wrap gap-2">
											{[
												{
													value: "30",
													label: tpg("expiry_options.minutes_30"),
												},
												{ value: "60", label: tpg("expiry_options.hour_1") },
												{ value: "180", label: tpg("expiry_options.hours_3") },
												{ value: "300", label: tpg("expiry_options.hours_5") },
												{ value: "720", label: tpg("expiry_options.hours_12") },
											].map((option) => {
												const isSelected = field.value === option.value;
												return (
													<Badge
														key={option.value}
														variant={isSelected ? "default" : "outline"}
														className={cn(
															"cursor-pointer text-sm py-1.5 px-3 transition-all",
															isSelected
																? "ring-2 ring-primary ring-offset-1 shadow-sm"
																: "hover:bg-muted text-muted-foreground shadow-inner bg-sidebar",
														)}
														onClick={() => field.onChange(option.value)}
													>
														{option.label}
													</Badge>
												);
											})}
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					<FormField
						control={form.control}
						name="additionalSettings.voucherCode"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("voucher.label")}{" "}
									<span className="text-sm italic font-light">
										({t("common.optional")})
									</span>
								</FormLabel>
								<div className="flex gap-2">
									<FormControl>
										<Input
											{...field}
											type="text"
											autoComplete="off"
											placeholder={t("voucher.placeholder")}
											className="shadow-inner bg-sidebar"
											onKeyDown={(event) => {
												if (event.key === "Enter") {
													event.preventDefault();
													void checkVoucher();
												}
											}}
										/>
									</FormControl>
									<Button
										type="button"
										variant="outline"
										disabled={
											!voucherCode?.trim() ||
											!selectedPackage ||
											voucher.status === "loading"
										}
										onClick={() => void checkVoucher()}
									>
										{voucher.status === "loading" ? (
											<LoaderIcon className="animate-spin" />
										) : null}
										{t("voucher.check")}
									</Button>
								</div>

								<VoucherPreviewLine
									voucher={voucher}
									currency={selectedPackage?.currency}
									needsPackage={!!voucherCode?.trim() && !selectedPackage}
								/>
								<FormMessage />
							</FormItem>
						)}
					/>
				</>
			)}
		</FormStepItemContainer>
	);
}

/** What the last voucher check said, in a single line under the field. */
function VoucherPreviewLine({
	voucher,
	currency,
	needsPackage,
}: {
	voucher: ReturnType<typeof useBookingPrice>["voucher"];
	currency?: string;
	needsPackage: boolean;
}) {
	const { t } = useTranslation("appointments-form");
	const money = (value: number) =>
		formatCurrency(value, { currency: currency || "IDR" });

	if (needsPackage) {
		return (
			<p className="text-xs text-muted-foreground">
				{t("voucher.needs_package")}
			</p>
		);
	}
	if (voucher.status === "valid") {
		return (
			<output className="flex items-center gap-1.5 text-xs font-medium">
				<CircleCheckBig className="size-3.5 shrink-0 text-primary" />
				{t("voucher.valid", {
					discount: money(voucher.discountAmount),
					total: money(voucher.finalPrice),
				})}
			</output>
		);
	}
	if (voucher.status === "invalid" || voucher.status === "error") {
		return (
			<p className="text-xs font-medium text-destructive" role="alert">
				{voucher.message}
			</p>
		);
	}
	return null;
}

// Referral source, partner booking and notes live on step 1 next to the patient
// record: they describe the booking, not the appointment settings. They keep the
// `additionalSettings` namespace, so step 1 has to trigger their paths by hand
// (see onSubmit in use-appointment-form).
export function BookingSourceForm() {
	const { t: tbs } = useTranslation("appointments-form", {
		keyPrefix: "booking_source",
	});
	const { mode } = useFormProvider();
	const { isPartnerBooking } = usePartnerBookingSelection();
	const { isCustomFisiohomePartner } = usePartnerNameSelection();
	const { isCustomReferral } = usePatientReferralSource();
	const { form, additionalFormOptions, watchAdditionalSettingsValue } =
		useAdditionalSettingsForm();

	return (
		<Fragment>
			<p className="mb-2 text-xs tracking-wider uppercase text-muted-foreground col-span-full">
				{tbs("label")}
			</p>

			{mode === "new" ? (
				<>
					<Deferred
						data={["optionsData"]}
						fallback={
							<div className="flex flex-col self-end gap-3 col-span-full">
								<Skeleton className="w-10 h-4 rounded-md" />
								<div className="grid grid-cols-1 gap-4 md:grid-cols-5">
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
								</div>
							</div>
						}
					>
						<FormField
							control={form.control}
							name="additionalSettings.referralSource"
							render={({ field }) => (
								<FormItem className="space-y-3 col-span-full">
									<FormLabel>
										Referral Source{" "}
										<span className="text-sm italic font-light">
											- (optional)
										</span>
									</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={field.onChange}
											value={field.value}
											className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-x-6"
										>
											{additionalFormOptions.referralSources.map((option) => (
												<FormItem
													key={option}
													className="flex items-center space-x-3 space-y-0"
												>
													<FormControl>
														<RadioGroupItem value={option} />
													</FormControl>
													<FormLabel className="flex items-center gap-1 font-normal">
														<span>{getPatientReferralLabel(option)}</span>
													</FormLabel>
												</FormItem>
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</Deferred>

					{isCustomReferral && (
						<Deferred
							data={["optionsData"]}
							fallback={
								<div className="grid grid-cols-1 gap-4 col-span-full">
									<Skeleton className="relative w-full rounded-md h-9" />
								</div>
							}
						>
							<FormField
								control={form.control}
								name="additionalSettings.customReferralSource"
								render={({ field }) => (
									<FormItem className="col-span-full motion-preset-rebound-down">
										<FormControl>
											<Input
												{...field}
												type="text"
												placeholder="Please specify the referral source"
												className="shadow-inner bg-sidebar"
											/>
										</FormControl>

										<FormMessage />
									</FormItem>
								)}
							/>
						</Deferred>
					)}

					<FormField
						control={form.control}
						name="additionalSettings.fisiohomePartnerBooking"
						render={({ field }) => (
							<FormItem className="space-y-3 col-span-full">
								<FormLabel>{tbs("partner_booking_label")}</FormLabel>
								<FormControl>
									<RadioGroup
										onValueChange={(value) => field.onChange(value === "true")}
										value={String(!!field.value)}
										orientation="horizontal"
										className="flex flex-row items-center gap-5"
									>
										{[true, false].map((booleanValue) => (
											<FormItem
												key={String(booleanValue)}
												className="flex items-center space-x-3 space-y-0"
											>
												<FormControl>
													<RadioGroupItem value={String(booleanValue)} />
												</FormControl>
												<FormLabel className="font-normal">
													{booleanValue ? "Yes" : "No"}
												</FormLabel>
											</FormItem>
										))}
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{isPartnerBooking && (
						<div
							className={cn(
								"col-span-full",
								isCustomFisiohomePartner ? "flex gap-3 " : "",
							)}
						>
							<FormField
								control={form.control}
								name="additionalSettings.fisiohomePartnerName"
								render={({ field }) => (
									<FormItem className="motion-preset-rebound-down">
										<FormLabel className="text-nowrap">
											Fisiohome Partner
										</FormLabel>
										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														className={cn(
															"relative w-full flex justify-between font-normal bg-sidebar shadow-inner",
															!field.value && "text-muted-foreground",
														)}
													>
														{field.value
															? getFisiohomePartnerLabel(field.value)
															: "Select fisiohome partner"}
														<ChevronsUpDown className="opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent
												className="p-0 w-[300px]"
												align="start"
												side="bottom"
											>
												<Command>
													<CommandInput
														placeholder="Search fisiohome partner..."
														className="h-9"
													/>
													<CommandList>
														<CommandEmpty>
															No fisiohome partner found.
														</CommandEmpty>
														<CommandGroup>
															{additionalFormOptions.fisiohomePartnerNames.map(
																(partner) => (
																	<CommandItem
																		value={getFisiohomePartnerLabel(partner)}
																		key={partner}
																		onSelect={() => {
																			form.setValue(
																				"additionalSettings.fisiohomePartnerName",
																				partner,
																				{ shouldValidate: true },
																			);
																		}}
																	>
																		{getFisiohomePartnerLabel(partner)}
																		<Check
																			className={cn(
																				"ml-auto",
																				partner === field.value
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																	</CommandItem>
																),
															)}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>

										<FormMessage />
									</FormItem>
								)}
							/>

							{isCustomFisiohomePartner && (
								<FormField
									control={form.control}
									name="additionalSettings.customFisiohomePartnerName"
									render={({ field }) => (
										<FormItem className="w-full motion-preset-rebound-down">
											<FormLabel className="invisible">Other Partner</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="text"
													placeholder="Please specify the fisiohome partner name"
													className="relative shadow-inner bg-sidebar"
												/>
											</FormControl>

											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					)}
				</>
			) : (
				<div className="grid grid-cols-1 gap-4 p-3 text-sm border rounded-md shadow-inner md:grid-cols-3 border-input bg-sidebar col-span-full">
					<div>
						<p className="font-light">Referral Source:</p>
						<p className="text-pretty">
							{watchAdditionalSettingsValue?.customReferralSource ||
								watchAdditionalSettingsValue?.referralSource ||
								"N/A"}
						</p>
					</div>
					<div>
						<p className="font-light">Booking Partner:</p>
						<p className="text-pretty">
							{watchAdditionalSettingsValue?.customFisiohomePartnerName ||
								watchAdditionalSettingsValue?.fisiohomePartnerName ||
								"N/A"}
						</p>
					</div>
					<div>
						<p className="font-light">Voucher:</p>
						<p className="text-pretty">
							{watchAdditionalSettingsValue?.voucherCode || "N/A"}
						</p>
					</div>
				</div>
			)}

			<FormField
				control={form.control}
				name="additionalSettings.notes"
				render={({ field }) => (
					<FormItem className="col-span-full">
						<FormLabel>
							Notes{" "}
							<span className="text-sm italic font-light">- (optional)</span>
						</FormLabel>
						<FormControl>
							<Textarea
								{...field}
								placeholder="Enter the additional appointment notes..."
								className="shadow-inner bg-sidebar"
							/>
						</FormControl>

						<FormDescription>
							Any notes that are relevant to this appointment.
						</FormDescription>

						<FormMessage />
					</FormItem>
				)}
			/>
		</Fragment>
	);
}

export function ReviewForm() {
	const { sections, errorsServerValidation, onEdit } = useReviewForm();
	const { t } = useTranslation("appointments-form");

	return (
		<FormStepItemContainer className="!grid-cols-1 gap-6">
			{sections.map((section) => (
				<div key={section.key} className="grid gap-2 text-sm">
					<div className="flex items-center justify-between">
						<p className="text-sm font-medium tracking-tight">
							{section.title}
						</p>
						<Button
							type="button"
							variant="link"
							effect="expandIcon"
							iconPlacement="left"
							className="p-0"
							icon={Pencil}
							onClick={(event) => {
								event.preventDefault();
								onEdit(section.stepValue);
							}}
						>
							{t("button.edit")}
						</Button>
					</div>

					<Table className="border rounded-lg border-border">
						<TableBody>
							{section.subs.map((sub, index) => (
								<TableRow key={`${sub.key}-${index}`}>
									<TableCell className="p-2 font-light tracking-tight md:p-4">
										{sub.title}
									</TableCell>
									<TableCell className="p-2 font-medium text-right break-words md:p-4 text-clip text-pretty">
										{sub.value === "N/A" ? (
											<span className="text-muted-foreground">—</span>
										) : (
											sub.value
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			))}

			{/* for showing the alert error if there's any server validation error */}
			{!!errorsServerValidation?.length && (
				<Alert variant="destructive">
					<AlertCircle className="size-4" />
					<AlertTitle className="text-xs">Error</AlertTitle>
					<AlertDescription className="text-xs">
						<ul className="list-disc">
							{errorsServerValidation?.map((error) => (
								<li key={error}>{error}</li>
							))}
						</ul>
					</AlertDescription>
				</Alert>
			)}
		</FormStepItemContainer>
	);
}
